import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaBus, FaRoad, FaClock, FaRupeeSign, FaArrowRight, 
  FaPhoneAlt, FaWhatsapp, FaUsers, FaShieldAlt, 
  FaSnowflake, FaWifi, FaChargingStation, FaMapMarkedAlt 
} from 'react-icons/fa';
import { GiCarSeat, GiGearStick, GiSteeringWheel, GiCargoCrane } from 'react-icons/gi';
import { MdLocalGasStation, MdAirlineSeatRecline, MdLuggage } from 'react-icons/md';
import { BiWind } from 'react-icons/bi';
import { buildLandingBookingState, loadLandingPageVehicles } from './vehicleLandingPageData';

const tempoImageMap = {
  tempo: 'https://buscdn.cardekho.com/in/force/traveller-3050/force-traveller-3050.jpg?impolicy=resize&imwidth=480',
  default: 'https://5.imimg.com/data5/SELLER/Default/2024/2/387850843/WU/FM/XD/130609265/17-seater-tempo-traveller-rental-service.webp',
};

const TempoTravellerPage = () => {
  // Tab state: 'local' or 'outstation'
  const [activeTab, setActiveTab] = useState('local');
  const [apiTempoList, setApiTempoList] = useState({ outstation: [], local: [] });
  const [isLoadingApiVehicles, setIsLoadingApiVehicles] = useState(true);
  const navigate = useNavigate();

  // Local Tempo Traveller Data
  const tempoLocal = [
    {
      id: 'tempo12',
      name: 'Tempo Traveller',
      variant: '12+1 Seater',
      seats: '12+1',
      price: 5000,
      baseKm: 80,
      baseHours: 8,
      extraKm: 28,
      extraHour: 400,
      fuel: 'Diesel',
      transmission: 'Manual',
      ac: 'Premium AC',
      luggage: 'Large Boot Space',
      image: 'https://yashikaholidays.in/wp-content/uploads/2026/01/263c9727-c870-4d92-89ec-85e8e3492839.png'
    },
    {
      id: 'tempo13',
      name: 'Tempo Traveller',
      variant: '13+1 Seater',
      seats: '13+1',
      price: 5500,
      baseKm: 80,
      baseHours: 8,
      extraKm: 30,
      extraHour: 400,
      fuel: 'Diesel',
      transmission: 'Manual',
      ac: 'Premium AC',
      luggage: 'Extra Large Boot',
      image: 'https://buscdn.cardekho.com/in/force/traveller-3050/force-traveller-3050.jpg?impolicy=resize&imwidth=480'
    },
    {
      id: 'tempoLuxury',
      name: 'Tempo Traveller Luxury',
      variant: '7/9/12 Seater',
      seats: '7, 9 or 12',
      price: 5500,
      baseKm: 80,
      baseHours: 8,
      extraKm: 32,
      extraHour: 500,
      fuel: 'Diesel',
      transmission: 'Manual',
      ac: 'Luxury AC',
      luggage: 'Premium Spacious',
      features: 'Pushback seats, LED lighting, Wifi ready',
      image: 'https://5.imimg.com/data5/SELLER/Default/2024/2/387850843/WU/FM/XD/130609265/17-seater-tempo-traveller-rental-service.webp'
    }
  ];

  // Outstation Tempo Traveller Data
  const tempoOutstation = [
    {
      id: 'tempo12-out',
      name: 'Tempo Traveller',
      variant: '12+1 Seater',
      seats: '12+1',
      pricePerKm: 23,
      minKms: 300,
      driverBeta: 500,
      fuel: 'Diesel',
      transmission: 'Manual',
      ac: 'Premium AC',
      image: 'https://5.imimg.com/data5/SELLER/Default/2024/2/387850843/WU/FM/XD/130609265/17-seater-tempo-traveller-rental-service.webp'
    },
    {
      id: 'tempo13-out',
      name: 'Tempo Traveller',
      variant: '13+1 Seater',
      seats: '13+1',
      pricePerKm: 23,
      minKms: 300,
      driverBeta: 500,
      fuel: 'Diesel',
      transmission: 'Manual',
      ac: 'Premium AC',
      image: 'https://punetours.com/wp-content/uploads/2018/07/Pune-Tours-Weekend-Tempo-Traveller.jpg'
    }
  ];

  useEffect(() => {
    let isMounted = true;

    const loadVehicles = async () => {
      try {
        setIsLoadingApiVehicles(true);
        const nextVehicles = await loadLandingPageVehicles('tempo', tempoImageMap);
        if (isMounted) setApiTempoList(nextVehicles);
      } catch (error) {
        if (isMounted) setApiTempoList({ outstation: [], local: [] });
      } finally {
        if (isMounted) setIsLoadingApiVehicles(false);
      }
    };

    loadVehicles();

    return () => {
      isMounted = false;
    };
  }, []);

  const currentData = apiTempoList[activeTab]?.length
    ? apiTempoList[activeTab]
    : activeTab === 'local'
      ? tempoLocal
      : tempoOutstation;

  const handleBookNow = (tempo) => {
    const apiBookingState = buildLandingBookingState(tempo, activeTab);
    if (apiBookingState) {
      navigate('/booking', { state: apiBookingState });
      return;
    }

    const isLocal = activeTab === 'local';
    const vehicleData = {
      id: tempo.id,
      name: `${tempo.name} ${tempo.variant}`,
      variant: tempo.variant,
      type: 'Tempo Traveller',
      category: 'Tempo Traveller',
      seats: tempo.seats,
      fuel: tempo.fuel,
      transmission: tempo.transmission,
      ac: tempo.ac,
      luggage: tempo.luggage,
      image: tempo.image,
      features: tempo.features,
      price: isLocal ? tempo.price : tempo.pricePerKm,
      baseKm: tempo.baseKm,
      baseHours: tempo.baseHours,
      extraKm: tempo.extraKm,
      extraHour: tempo.extraHour,
      pricePerKm: tempo.pricePerKm,
      driverBeta: tempo.driverBeta,
      minKms: tempo.minKms,
    };
    const bookingData = {
      vehicle: vehicleData,
      route: {
        routeName: `${tempo.name} ${tempo.variant} ${isLocal ? 'local package' : 'outstation package'}`,
        pickup: '',
        dropoff: '',
        distance: isLocal ? tempo.baseKm : tempo.minKms,
        estimatedTime: isLocal ? `${tempo.baseHours} hours` : '',
        price: isLocal ? tempo.price : tempo.pricePerKm,
        tripDirection: isLocal ? 'Local' : 'Outstation',
        tripType: activeTab,
      },
      pricing: isLocal
        ? {
            basePrice: tempo.price,
            includedKms: tempo.baseKm,
            includedHours: tempo.baseHours,
            extraPerKm: tempo.extraKm,
            extraPerHour: tempo.extraHour,
          }
        : {
            basePrice: tempo.pricePerKm,
            pricePerKm: tempo.pricePerKm,
            driverBata: tempo.driverBeta,
            minKms: tempo.minKms,
          },
    };

    navigate('/booking', {
      state: {
        selectedVehicleData: vehicleData,
        initialRouteData: bookingData,
        tripType: isLocal ? 'Local Taxi' : 'Outstation',
      },
    });
  };

  return (
    <div className="min-h-screen bg-white text-black font-sans">
      {/* Hero Section with Tempo Theme */}
      <div className="relative bg-gradient-to-r from-white via-white to-yellow-50 border-b-4 border-yellow-500">
        <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
          <div className="text-center md:text-left md:flex md:justify-between md:items-end">
            <div>
              <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
                <FaBus className="text-yellow-600" />
                <span>Group Travel Experts</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">
                Tempo Traveller
                <br />
                <span className="text-yellow-600">Rentals</span>
              </h1>
              <p className="text-gray-600 mt-3 max-w-lg">
                Perfect for group trips, family outings, and corporate events. 
                Spacious, comfortable, and reliable fleet with professional drivers.
              </p>
            </div>
            {/* <div className="flex gap-3 mt-6 md:mt-0">
              <button className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-xl font-semibold transition shadow-md">
                <FaWhatsapp size={18} />
                <span>WhatsApp</span>
              </button>
              <button className="flex items-center gap-2 border-2 border-yellow-500 text-yellow-700 hover:bg-yellow-50 px-5 py-2.5 rounded-xl font-semibold transition">
                <FaPhoneAlt size={14} />
                <span>Call Now</span>
              </button>
            </div> */}
          </div>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="max-w-7xl mx-auto px-4 mt-6 mb-2">
        <div className="flex flex-wrap gap-3 border-b border-gray-200 pb-2">


           <button
            onClick={() => setActiveTab('outstation')}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-t-xl font-bold text-base transition-all ${
              activeTab === 'outstation'
                ? 'bg-yellow-500 text-white shadow-md -mb-[2px] border-b-2 border-yellow-600'
                : 'bg-gray-100 text-gray-700 hover:bg-yellow-100'
            }`}
          >
            <FaMapMarkedAlt className="text-sm" />
            OUTSTATION / ONE WAY
          </button>
          <button
            onClick={() => setActiveTab('local')}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-t-xl font-bold text-base transition-all ${
              activeTab === 'local'
                ? 'bg-yellow-500 text-white shadow-md -mb-[2px] border-b-2 border-yellow-600'
                : 'bg-gray-100 text-gray-700 hover:bg-yellow-100'
            }`}
          >
            <FaClock className="text-sm" />
            LOCAL / ROUND TRIP
          </button>
         
        </div>
        <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
          <span className="inline-block w-1.5 h-1.5 bg-yellow-500 rounded-full"></span>
          {activeTab === 'local'
            ? 'Local packages: 8h/80km included. Toll and parking extra as per actuals.'
            : 'Outstation: 300km/day included • Ideal for hill stations, temple tours, long drives'}
        </p>
      </div>

      {/* Cards Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {isLoadingApiVehicles && (
          <div className="mb-4 rounded-xl bg-yellow-50 px-4 py-3 text-sm font-semibold text-yellow-800">
            Loading latest Tempo Traveller rates...
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentData.map((tempo) => (
            <div
              key={tempo.id}
              className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 flex flex-col"
            >
              {/* Image Container */}
              <div className="relative h-52 bg-gradient-to-br from-gray-50 to-yellow-50 overflow-hidden">
                <img
                  src={tempo.image}
                  alt={tempo.name}
                  className="w-full h-full object-cover transition duration-500 hover:scale-105"
                  onError={(e) => {
                    e.currentTarget.src = 'https://imgd.aeplcdn.com/1056x594/n/cw/ec/123405/force-traveller-exterior-right-front-three-quarter-3.jpeg?isig=0&q=80';
                  }}
                />
                <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm text-white text-xs font-bold px-2 py-1 rounded-lg flex items-center gap-1">
                  <GiCarSeat size={12} />
                  <span>{tempo.seats} Seats</span>
                </div>
                <div className="absolute top-3 right-3 bg-yellow-500 text-black font-extrabold text-xs px-2.5 py-1 rounded-full shadow-md">
                  {activeTab === 'local' ? 'Best for City' : 'Outstation Trip'}
                </div>
                {tempo.id === 'tempoLuxury' && activeTab === 'local' && (
                  <div className="absolute bottom-3 left-3 bg-black/70 text-yellow-300 text-xs font-bold px-2 py-1 rounded-lg flex items-center gap-1">
                    <FaShieldAlt size={10} />
                    <span>Luxury Series</span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="text-xl font-extrabold text-gray-900 tracking-tight">{tempo.name}</h3>
                    <p className="text-sm text-yellow-600 font-semibold">{tempo.variant}</p>
                  </div>
                  <div className="bg-yellow-100 p-2 rounded-full">
                    <FaBus className="text-yellow-600 text-lg" />
                  </div>
                </div>

                {/* Specs row */}
                <div className="flex flex-wrap gap-2 text-xs text-gray-600 mb-4 border-b border-gray-100 pb-3">
                  <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-md">
                    <MdLocalGasStation className="text-yellow-600" />
                    <span>{tempo.fuel}</span>
                  </div>
                  <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-md">
                    <GiGearStick className="text-yellow-600" />
                    <span>{tempo.transmission}</span>
                  </div>
                  <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-md">
                    <BiWind className="text-yellow-600" />
                    <span>{tempo.ac}</span>
                  </div>
                  <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-md">
                    <MdLuggage className="text-yellow-600" />
                    <span>Luggage</span>
                  </div>
                </div>

                {/* PRICE Section */}
                <div className="mb-4">
                  {activeTab === 'local' ? (
                    <>
                      <div className="flex items-baseline gap-1 flex-wrap">
                        <span className="text-3xl font-black text-yellow-600">
                          <FaRupeeSign className="inline text-2xl mr-0.5" />
                          {tempo.price}
                        </span>
                        <span className="text-gray-500 text-sm font-medium">/ day</span>
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-sm font-semibold text-gray-700 bg-yellow-50 inline-flex px-3 py-1 rounded-full">
                        <FaClock className="text-yellow-600" />
                        <span>{tempo.baseHours}h | {tempo.baseKm}km included</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1 flex-wrap">
                        <span className="text-3xl font-black text-yellow-600">
                          <FaRupeeSign className="inline text-2xl mr-0.5" />
                          {tempo.pricePerKm}
                        </span>
                        <span className="text-gray-500 text-sm font-medium">/ km</span>
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-sm font-semibold text-gray-700 bg-yellow-50 inline-flex px-3 py-1 rounded-full">
                        <FaRoad className="text-yellow-600" />
                        <span>Min {tempo.minKms} km/day</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Extra Details based on tab */}
                {activeTab === 'local' ? (
                  <div className="space-y-2 text-sm bg-gray-50 p-3 rounded-xl mb-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 flex items-center gap-1">
                        <FaRoad className="text-yellow-500 text-xs" /> Extra km:
                      </span>
                      <span className="font-bold text-black">
                        <FaRupeeSign className="inline text-xs" /> {tempo.extraKm}/km
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 flex items-center gap-1">
                        <FaClock className="text-yellow-500 text-xs" /> Extra hour:
                      </span>
                      <span className="font-bold text-black">
                        <FaRupeeSign className="inline text-xs" /> {tempo.extraHour}/hr
                      </span>
                    </div>
                    <div className="text-xs text-red-600 bg-red-50 p-2 rounded-md mt-1 flex items-start gap-1">
                      <span className="font-bold">Note:</span> Toll and parking extra as per actuals.
                    </div>
                    {tempo.features && (
                      <div className="text-xs text-yellow-700 bg-yellow-50 p-2 rounded-md mt-1 flex items-center gap-1">
                        <FaWifi size={10} />
                        <span>{tempo.features}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 text-sm bg-gray-50 p-3 rounded-xl mb-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 flex items-center gap-1">
                        <FaRoad className="text-yellow-500 text-xs" /> Rate per km:
                      </span>
                      <span className="font-bold text-black">
                        <FaRupeeSign className="inline text-xs" /> {tempo.pricePerKm}/km
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 flex items-center gap-1">
                        <FaMapMarkedAlt className="text-yellow-500 text-xs" /> Min. km/day:
                      </span>
                      <span className="font-bold">{tempo.minKms} km</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 flex items-center gap-1">
                        <GiSteeringWheel className="text-yellow-500 text-xs" /> Driver Bata:
                      </span>
                      <span className="font-bold">
                        <FaRupeeSign className="inline text-xs" /> {tempo.driverBeta}/day
                      </span>
                    </div>
                    <div className="text-xs text-red-600 bg-red-50 p-2 rounded-md mt-1 flex items-start gap-1">
                      <span className="font-bold">⚠️</span> Toll, parking & state permit extra (actual charges)
                    </div>
                  </div>
                )}

                {/* CTA Button */}
                <button
                  onClick={() => handleBookNow(tempo)}
                  className="mt-2 w-full bg-yellow-500 hover:bg-yellow-600 text-black font-extrabold py-3 rounded-xl flex items-center justify-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg group"
                >
                  <span>Book {tempo.variant}</span>
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform text-sm" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Why Choose Us Section */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-gradient-to-r from-yellow-50 to-white rounded-2xl p-6 border border-yellow-100">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FaShieldAlt className="text-yellow-600" />
            Why Choose Our Tempo Traveller?
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2 text-sm">
              <FaUsers className="text-yellow-600" />
              <span>Professional Drivers</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <FaSnowflake className="text-yellow-600" />
              <span>Perfect AC</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <MdLuggage className="text-yellow-600" />
              <span>Ample Luggage Space</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <FaShieldAlt className="text-yellow-600" />
              <span>Insurance Covered</span>
            </div>
          </div>
        </div>
      </div>

      {/* Important Notes Disclaimer */}
      <div className="max-w-7xl mx-auto px-4 pb-4">
        <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-xl p-4 text-sm text-gray-700">
          <p className="font-bold text-black mb-2 flex items-center gap-2">
            <FaBus className="text-yellow-600" />
            Important Information:
          </p>
          <ul className="space-y-1 text-gray-700 ml-4">
            <li>• <span className="font-semibold">Local Package:</span> 8 hours / 80 km included. Extra km, extra hour, toll, and parking charges are extra.</li>
            <li>• <span className="font-semibold">Outstation Package:</span> 300 km per day included. Driver bata: ₹400-500/day depending on vehicle.</li>
            <li>• Toll, parking, interstate permit, and night charges (if applicable) are extra as per actuals.</li>
            <li>• All prices are inclusive of GST and driver allowance (except bata for outstation).</li>
            <li>• Luxury tempo includes pushback seats, LED interior lights, and premium sound system.</li>
          </ul>
        </div>
      </div>

      {/* Footer */}
     
    </div>
  );
};

export default TempoTravellerPage;
